import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import employeeService, { employeeKeys } from '@/services/employee.service';

export type EmployeeOption = { value: string; label: string; raw?: any };

/**
 * Shared active-employee Select options for HRMS forms/filters.
 */
export function useEmployeeOptions(params?: { employment_status?: string | null; enabled?: boolean }) {
  const employment_status =
    params && 'employment_status' in params ? params.employment_status ?? undefined : 'Active';
  const enabled = params?.enabled !== false;
  const listParams = employment_status ? { employment_status } : {};

  const query = useQuery({
    queryKey: employeeKeys.list(listParams),
    queryFn: () => employeeService.list(listParams),
    enabled,
    staleTime: 60_000,
  });

  const options: EmployeeOption[] = useMemo(
    () =>
      (query.data ?? []).map((e) => ({
        value: e.id,
        label: `${e.employee_code} — ${e.first_name} ${e.last_name}`,
        raw: e,
      })),
    [query.data]
  );

  const labelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  return {
    employees: query.data ?? [],
    options,
    labelById,
    isLoading: query.isLoading,
    empLabel: (id?: string | null) => (id ? labelById.get(id) || id : '—'),
  };
}
